import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import './ERByThreeVsPy.css';
import { BIGQUERY_CONFIG } from '../config';

import { API_BASE_URL } from '../config';

// Use config values
const PROJECT_ID = BIGQUERY_CONFIG.PROJECT;
const DATASET_NAME = BIGQUERY_CONFIG.DATASET;

function SNFByThreeVsPy() {
  const { token, getAuthHeader } = useAuth();
  const [summaryTable, setSummaryTable] = useState({
    ESRD: {},
    DIS: {},
    AD: {},
    ND: {},
    TOTAL: {}
  });
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Toggle states for charts
  const [cohortToggle, setCohortToggle] = useState('TOTAL'); // 'ESRD', 'DIS', 'AD', 'ND', 'TOTAL', 'ALL'

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching SNF PY General data from:', `${API_BASE_URL}/api/snf/by3-vs-py?project=${PROJECT_ID}&dataset=${DATASET_NAME}`);
      console.log('Auth header:', getAuthHeader());
      
      const response = await axios.get(
        `${API_BASE_URL}/api/snf/by3-vs-py?project=${PROJECT_ID}&dataset=${DATASET_NAME}`,
        { 
          timeout: 180000
        }
      );

      console.log('Response received:', response.data);

      if (response.data.success) {
        setSummaryTable(response.data.summary_table || {ESRD: {}, DIS: {}, AD: {}, ND: {}, TOTAL: {}});
        setMonthlyData(response.data.monthly_data || []);
      } else {
        setError('Failed to load data: ' + (response.data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError(error.response?.data?.detail || error.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const getMonthName = (yr_mo) => {
    const yr_mo_str = String(yr_mo || '');
    if (!yr_mo_str || yr_mo_str.length < 6) return yr_mo_str;
    const year = yr_mo_str.substring(0, 4);
    const month = yr_mo_str.substring(4, 6);
    const monthIndex = parseInt(month) - 1;
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[monthIndex]} ${year}`;
  };

  const cohortColors = {
    'ESRD': '#3b82f6',
    'DIS': '#10b981',
    'AD': '#f59e0b',
    'ND': '#8b5cf6',
    'TOTAL': '#ef4444'
  };

  const getMonthlyAdmitsChartData = () => {
    const months = [...new Set(monthlyData.map(item => item.month))].sort();
    
    if (cohortToggle === 'ALL') {
      return months.map(month => {
        const dataPoint = { month: getMonthName(month) };
        ['ESRD', 'DIS', 'AD', 'ND', 'TOTAL'].forEach(cohort => {
          const cohortData = monthlyData.find(item => item.month === month && item.cohort === cohort);
          dataPoint[cohort] = cohortData ? (cohortData.admits || 0) : 0;
        });
        return dataPoint;
      });
    } else {
      return months.map(month => {
        const cohortData = monthlyData.find(item => item.month === month && item.cohort === cohortToggle);
        return {
          month: getMonthName(month),
          admits: cohortData ? (cohortData.admits || 0) : 0
        };
      });
    }
  };

  const getMonthlyAdmitsADKChartData = () => {
    const months = [...new Set(monthlyData.map(item => item.month))].sort();
    
    if (cohortToggle === 'ALL') {
      return months.map(month => {
        const dataPoint = { month: getMonthName(month) };
        ['ESRD', 'DIS', 'AD', 'ND', 'TOTAL'].forEach(cohort => {
          const cohortData = monthlyData.find(item => item.month === month && item.cohort === cohort);
          dataPoint[cohort] = cohortData ? (cohortData.admits_adk || 0) : 0;
        });
        return dataPoint;
      });
    } else {
      return months.map(month => {
        const cohortData = monthlyData.find(item => item.month === month && item.cohort === cohortToggle);
        return {
          month: getMonthName(month),
          adk: cohortData ? (cohortData.admits_adk || 0) : 0
        };
      });
    }
  };

  const getMonthlyPMPYChartData = () => {
    const months = [...new Set(monthlyData.map(item => item.month))].sort();
    
    if (cohortToggle === 'ALL') {
      return months.map(month => {
        const dataPoint = { month: getMonthName(month) };
        ['ESRD', 'DIS', 'AD', 'ND', 'TOTAL'].forEach(cohort => {
          const cohortData = monthlyData.find(item => item.month === month && item.cohort === cohort);
          dataPoint[cohort] = cohortData ? (cohortData.pmpy || 0) : 0;
        });
        return dataPoint;
      });
    } else {
      return months.map(month => {
        const cohortData = monthlyData.find(item => item.month === month && item.cohort === cohortToggle);
        return {
          month: getMonthName(month),
          pmpy: cohortData ? (cohortData.pmpy || 0) : 0
        };
      });
    }
  };

  const getMonthlyCostChartData = () => {
    const months = [...new Set(monthlyData.map(item => item.month))].sort();
    
    if (cohortToggle === 'ALL') {
      return months.map(month => {
        const dataPoint = { month: getMonthName(month) };
        ['ESRD', 'DIS', 'AD', 'ND', 'TOTAL'].forEach(cohort => {
          const cohortData = monthlyData.find(item => item.month === month && item.cohort === cohort);
          dataPoint[cohort] = cohortData ? (cohortData.total_cost || 0) : 0;
        });
        return dataPoint;
      });
    } else {
      return months.map(month => {
        const cohortData = monthlyData.find(item => item.month === month && item.cohort === cohortToggle);
        return {
          month: getMonthName(month),
          cost: cohortData ? (cohortData.total_cost || 0) : 0
        };
      });
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip" style={{
          backgroundColor: 'white',
          border: '1px solid #ccc',
          padding: '10px',
          borderRadius: '4px'
        }}>
          <p className="label" style={{fontWeight: 'bold', marginBottom: '5px'}}>{label}</p>
          {payload.map((entry, index) => {
            let formattedValue = typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value;
            if (entry.name.toLowerCase().includes('cost') || entry.name.toLowerCase().includes('pmpy')) {
              formattedValue = `$${Math.round(entry.value).toLocaleString()}`;
            }
            return (
              <p key={index} style={{ color: entry.color, margin: '3px 0' }}>
                {entry.name}: {formattedValue}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  const formatCurrencyAxis = (value) => {
    return `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === 0) return '-';
    return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === 0) return '-';
    return `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  };

  const formatDecimal = (value, decimals = 3) => {
    if (value === null || value === undefined || value === 0) return '-';
    return value.toFixed(decimals);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined || value === 0) return '-';
    return `${value.toFixed(2)}%`;
  };

  if (loading) {
    return (
      <div style={{textAlign: 'center', padding: '80px'}}>
        <div style={{
          display: 'inline-block',
          width: '50px',
          height: '50px',
          border: '5px solid #f3f4f6',
          borderTop: '5px solid #4caf50',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '20px'
        }}></div>
        <div style={{fontSize: '18px', color: '#6b7280', fontWeight: '500'}}>Loading SNF PY General data...</div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{padding: '40px', backgroundColor: '#fee', borderRadius: '8px', margin: '20px'}}>
        <h2 style={{color: '#c33'}}>Error loading data</h2>
        <p>{error}</p>
        <button onClick={fetchDashboardData} style={{padding: '10px 20px', backgroundColor: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}>Retry</button>
      </div>
    );
  }

  return (
    <div className="er-by3-vs-py-container">
      <div className="er-by3-vs-py-content">
        <h1>🏥 SNF PY GENERAL</h1>

        {/* Summary Table */}
        <div className="summary-table-section">
          <table className="summary-table">
            <thead>
              <tr>
                <th>Metric</th>
                <th>ESRD</th>
                <th>DIS</th>
                <th>AD</th>
                <th>ND</th>
                <th>TOTAL</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Person Years</strong></td>
                <td>{formatNumber(summaryTable.ESRD?.person_years)}</td>
                <td>{formatNumber(summaryTable.DIS?.person_years)}</td>
                <td>{formatNumber(summaryTable.AD?.person_years)}</td>
                <td>{formatNumber(summaryTable.ND?.person_years)}</td>
                <td>{formatNumber(summaryTable.TOTAL?.person_years)}</td>
              </tr>
              <tr>
                <td><strong>Risk Score</strong></td>
                <td>{formatDecimal(summaryTable.ESRD?.risk_score, 3)}</td>
                <td>{formatDecimal(summaryTable.DIS?.risk_score, 3)}</td>
                <td>{formatDecimal(summaryTable.AD?.risk_score, 3)}</td>
                <td>{formatDecimal(summaryTable.ND?.risk_score, 3)}</td>
                <td>{formatDecimal(summaryTable.TOTAL?.risk_score, 3)}</td>
              </tr>
              <tr>
                <td><strong>SNF ADMITS</strong></td>
                <td>{formatNumber(summaryTable.ESRD?.admits)}</td>
                <td>{formatNumber(summaryTable.DIS?.admits)}</td>
                <td>{formatNumber(summaryTable.AD?.admits)}</td>
                <td>{formatNumber(summaryTable.ND?.admits)}</td>
                <td>{formatNumber(summaryTable.TOTAL?.admits)}</td>
              </tr>
              <tr>
                <td><strong>SNF AVG LOS</strong></td>
                <td>{formatDecimal(summaryTable.ESRD?.avg_los, 2)}</td>
                <td>{formatDecimal(summaryTable.DIS?.avg_los, 2)}</td>
                <td>{formatDecimal(summaryTable.AD?.avg_los, 2)}</td>
                <td>{formatDecimal(summaryTable.ND?.avg_los, 2)}</td>
                <td>{formatDecimal(summaryTable.TOTAL?.avg_los, 2)}</td>
              </tr>
              <tr style={{backgroundColor: '#d1fae5'}}>
                <td><strong>SNF PMPY</strong></td>
                <td>{formatCurrency(summaryTable.ESRD?.pmpy)}</td>
                <td>{formatCurrency(summaryTable.DIS?.pmpy)}</td>
                <td>{formatCurrency(summaryTable.AD?.pmpy)}</td>
                <td>{formatCurrency(summaryTable.ND?.pmpy)}</td>
                <td>{formatCurrency(summaryTable.TOTAL?.pmpy)}</td>
              </tr>
              <tr style={{backgroundColor: '#d1fae5'}}>
                <td><strong>SNF TOTAL COST</strong></td>
                <td>{formatCurrency(summaryTable.ESRD?.total_cost)}</td>
                <td>{formatCurrency(summaryTable.DIS?.total_cost)}</td>
                <td>{formatCurrency(summaryTable.AD?.total_cost)}</td>
                <td>{formatCurrency(summaryTable.ND?.total_cost)}</td>
                <td>{formatCurrency(summaryTable.TOTAL?.total_cost)}</td>
              </tr>
              <tr>
                <td><strong>SNF 30D README %</strong></td>
                <td>{formatPercent(summaryTable.ESRD?.readmit_30d_pct)}</td>
                <td>{formatPercent(summaryTable.DIS?.readmit_30d_pct)}</td>
                <td>{formatPercent(summaryTable.AD?.readmit_30d_pct)}</td>
                <td>{formatPercent(summaryTable.ND?.readmit_30d_pct)}</td>
                <td>{formatPercent(summaryTable.TOTAL?.readmit_30d_pct)}</td>
              </tr>
              <tr>
                <td><strong>COST PER ADMIT</strong></td>
                <td>{formatCurrency(summaryTable.ESRD?.cost_per_admit)}</td>
                <td>{formatCurrency(summaryTable.DIS?.cost_per_admit)}</td>
                <td>{formatCurrency(summaryTable.AD?.cost_per_admit)}</td>
                <td>{formatCurrency(summaryTable.ND?.cost_per_admit)}</td>
                <td>{formatCurrency(summaryTable.TOTAL?.cost_per_admit)}</td>
              </tr>
              <tr>
                <td><strong>COST PER DAY</strong></td>
                <td>{formatCurrency(summaryTable.ESRD?.cost_per_day)}</td>
                <td>{formatCurrency(summaryTable.DIS?.cost_per_day)}</td>
                <td>{formatCurrency(summaryTable.AD?.cost_per_day)}</td>
                <td>{formatCurrency(summaryTable.ND?.cost_per_day)}</td>
                <td>{formatCurrency(summaryTable.TOTAL?.cost_per_day)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Cohort Toggle */}
        <div className="chart-controls" style={{margin: '30px 0'}}>
          <div className="toggle-group">
            <h3 style={{marginBottom: '10px'}}>Cohort Selection:</h3>
            <div className="toggle-buttons">
              {['ALL', 'ESRD', 'DIS', 'AD', 'ND', 'TOTAL'].map(cohort => (
                <button
                  key={cohort}
                  className={`toggle-btn ${cohortToggle === cohort ? 'active' : ''}`}
                  onClick={() => setCohortToggle(cohort)}
                >
                  {cohort}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Monthly SNF Admits Chart */}
        <div className="chart-section">
          <h2>Monthly SNF Admits ({cohortToggle})</h2>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={getMonthlyAdmitsChartData()} margin={{ top: 5, right: 30, left: 70, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" />
              <YAxis type="number" allowDecimals={false} domain={[0, dataMax => Math.ceil(dataMax * 1.15)]} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {cohortToggle === 'ALL' ? (
                ['ESRD', 'DIS', 'AD', 'ND', 'TOTAL'].map(cohort => (
                  <Line
                    key={cohort}
                    type="monotone"
                    dataKey={cohort}
                    stroke={cohortColors[cohort]}
                    strokeWidth={2}
                    dot={{ fill: cohortColors[cohort], r: 4 }}
                    activeDot={{ r: 6 }}
                    name={cohort}
                  />
                ))
              ) : (
                <Line
                  type="monotone"
                  dataKey="admits"
                  stroke="#1e40af"
                  strokeWidth={2}
                  dot={{ fill: '#1e40af', r: 4 }}
                  activeDot={{ r: 6 }}
                  name="SNF Admits"
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly SNF Admits ADK Chart */}
        <div className="chart-section">
          <h2>Monthly SNF Admits ADK ({cohortToggle})</h2>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={getMonthlyAdmitsADKChartData()} margin={{ top: 5, right: 30, left: 70, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" />
              <YAxis type="number" allowDecimals={false} domain={[0, dataMax => Math.ceil(dataMax * 1.15)]} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {cohortToggle === 'ALL' ? (
                ['ESRD', 'DIS', 'AD', 'ND', 'TOTAL'].map(cohort => (
                  <Line
                    key={cohort}
                    type="monotone"
                    dataKey={cohort}
                    stroke={cohortColors[cohort]}
                    strokeWidth={2}
                    dot={{ fill: cohortColors[cohort], r: 4 }}
                    activeDot={{ r: 6 }}
                    name={cohort}
                  />
                ))
              ) : (
                <Line
                  type="monotone"
                  dataKey="adk"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={{ fill: '#10b981', r: 4 }}
                  activeDot={{ r: 6 }}
                  name="SNF Admits ADK"
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly SNF PMPY Chart */}
        <div className="chart-section">
          <h2>Monthly SNF PMPY ({cohortToggle})</h2>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={getMonthlyPMPYChartData()} margin={{ top: 5, right: 30, left: 70, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" />
              <YAxis type="number" allowDecimals={false} tickFormatter={formatCurrencyAxis} domain={[0, dataMax => Math.ceil(dataMax * 1.15)]} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {cohortToggle === 'ALL' ? (
                ['ESRD', 'DIS', 'AD', 'ND', 'TOTAL'].map(cohort => (
                  <Line
                    key={cohort}
                    type="monotone"
                    dataKey={cohort}
                    stroke={cohortColors[cohort]}
                    strokeWidth={2}
                    dot={{ fill: cohortColors[cohort], r: 4 }}
                    activeDot={{ r: 6 }}
                    name={cohort}
                  />
                ))
              ) : (
                <Line
                  type="monotone"
                  dataKey="pmpy"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={{ fill: '#f59e0b', r: 4 }}
                  activeDot={{ r: 6 }}
                  name="SNF PMPY"
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly SNF Cost Chart */}
        <div className="chart-section">
          <h2>Monthly SNF Total Cost ({cohortToggle})</h2>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={getMonthlyCostChartData()} margin={{ top: 5, right: 30, left: 70, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" />
              <YAxis type="number" allowDecimals={false} tickFormatter={formatCurrencyAxis} domain={[0, dataMax => Math.ceil(dataMax * 1.15)]} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {cohortToggle === 'ALL' ? (
                ['ESRD', 'DIS', 'AD', 'ND', 'TOTAL'].map(cohort => (
                  <Line
                    key={cohort}
                    type="monotone"
                    dataKey={cohort}
                    stroke={cohortColors[cohort]}
                    strokeWidth={2}
                    dot={{ fill: cohortColors[cohort], r: 4 }}
                    activeDot={{ r: 6 }}
                    name={cohort}
                  />
                ))
              ) : (
                <Line
                  type="monotone"
                  dataKey="cost"
                  stroke="#ef4444"
                  strokeWidth={2}
                  dot={{ fill: '#ef4444', r: 4 }}
                  activeDot={{ r: 6 }}
                  name="SNF Total Cost"
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export default SNFByThreeVsPy;
